import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import CustomSelect from '../components/CustomSelect';

interface ResidentAccount {
  id: number;
  residentId: number;
  residentName: string;
  balance: string;
  currency: string;
  updatedAt: string;
}

interface Transaction {
  id: number;
  accountId: number;
  transactionType: string;
  category: string;
  amount: string;
  balanceAfter: string;
  description: string;
  notes: string | null;
  createdBy: {
    id: number;
    name: string;
  };
  createdAt: string;
}

interface TransactionFormData {
  transactionType: string;
  category: string;
  amount: string;
  description: string;
  notes: string;
}

const Finance: React.FC = () => {
  const { isAdmin } = useAuth();
  const [accounts, setAccounts] = useState<ResidentAccount[]>([]);
  const [selectedResident, setSelectedResident] = useState<ResidentAccount | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionForm, setTransactionForm] = useState<TransactionFormData>({
    transactionType: 'deposit',
    category: 'other',
    amount: '',
    description: '',
    notes: ''
  });

  useEffect(() => {
    if (isAdmin) {
      fetchAccounts();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (selectedResident) {
      fetchTransactions(selectedResident.residentId);
    }
  }, [selectedResident]);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/finance/residents');
      if (response.data.success) {
        setAccounts(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedResident(response.data.data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (residentId: number) => {
    try {
      const response = await api.get(`/finance/residents/${residentId}/transactions?limit=100`);
      if (response.data.success) {
        setTransactions(response.data.data.transactions);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const handleSubmitTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedResident) return;

    try {
      await api.post(`/finance/residents/${selectedResident.residentId}/transactions`, {
        transactionType: transactionForm.transactionType,
        category: transactionForm.category,
        amount: parseFloat(transactionForm.amount),
        description: transactionForm.description,
        notes: transactionForm.notes || null
      });

      setShowTransactionModal(false);
      setTransactionForm({
        transactionType: 'deposit',
        category: 'other',
        amount: '',
        description: '',
        notes: ''
      });

      fetchAccounts();
      fetchTransactions(selectedResident.residentId);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create transaction');
    }
  };

  const getTransactionTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      deposit: 'bg-green-100 text-green-800',
      withdrawal: 'bg-orange-100 text-orange-800',
      purchase: 'bg-blue-100 text-blue-800',
      adjustment: 'bg-primary-100 text-primary-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTransactionTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      deposit: 'Deposit',
      withdrawal: 'Withdrawal',
      purchase: 'Purchase',
      adjustment: 'Adjustment'
    };
    return labels[type] || type;
  };

  const formatCurrency = (amount: string) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
        <p className="mt-2 text-gray-600">Only administrators can access the finance module.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading finance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Finance Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage residents' personal allowances and financial transactions
          </p>
        </div>
        {selectedResident && (
          <div className="mt-4 flex md:mt-0 md:ml-4">
            <button
              onClick={() => setShowTransactionModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              New Transaction
            </button>
          </div>
        )}
      </div>

      {/* Resident Accounts Grid */}
      <div className="bg-white card-list p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Resident Accounts</h3>
        {accounts.length === 0 ? (
          <p className="text-sm text-gray-500">No resident accounts found.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((account) => (
              <div
                key={account.id}
                onClick={() => setSelectedResident(account)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedResident?.id === account.id
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{account.residentName}</h4>
                    <p className="text-xs text-gray-500 mt-1">Account #{account.id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">{formatCurrency(account.balance)}</p>
                    <p className="text-xs text-gray-500">{account.currency}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Transaction History */}
      {selectedResident && (
        <div className="bg-white card-list overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Transaction History - {selectedResident.residentName}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Current Balance: <span className="font-semibold">{formatCurrency(selectedResident.balance)}</span>
            </p>
          </div>
          <div className="divide-y divide-gray-200">
            {transactions.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">No transactions found for this resident.</p>
              </div>
            ) : (
              transactions.map((transaction) => (
                <div key={transaction.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTransactionTypeColor(transaction.transactionType)}`}>
                          {getTransactionTypeLabel(transaction.transactionType)}
                        </span>
                        <h4 className="text-sm font-medium text-gray-900">{transaction.description}</h4>
                      </div>
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                        <span>Category: {transaction.category}</span>
                        <span>By: {transaction.createdBy.name}</span>
                        <span>{formatDate(transaction.createdAt)}</span>
                      </div>
                      {transaction.notes && (
                        <p className="mt-1 text-xs text-gray-600 italic">Note: {transaction.notes}</p>
                      )}
                    </div>
                    <div className="ml-4 text-right">
                      <p className={`text-lg font-bold ${
                        transaction.transactionType === 'deposit' || transaction.transactionType === 'adjustment'
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}>
                        {transaction.transactionType === 'deposit' || transaction.transactionType === 'adjustment'
                          ? '+' : '-'}
                        {formatCurrency(transaction.amount)}
                      </p>
                      <p className="text-xs text-gray-500">
                        Balance: {formatCurrency(transaction.balanceAfter)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* New Transaction Modal */}
      {showTransactionModal && selectedResident && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowTransactionModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowTransactionModal(false)}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmitTransaction}>
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    New Transaction - {selectedResident.residentName}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Transaction Type</label>
                      <div className="mt-1">
                        <CustomSelect
                          value={transactionForm.transactionType}
                          onChange={(v) => setTransactionForm({...transactionForm, transactionType: v})}
                          options={[
                            { value: 'deposit', label: 'Deposit' },
                            { value: 'withdrawal', label: 'Withdrawal' },
                            { value: 'purchase', label: 'Purchase' },
                            { value: 'adjustment', label: 'Adjustment' },
                          ]}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Category</label>
                      <div className="mt-1">
                        <CustomSelect
                          value={transactionForm.category}
                          onChange={(v) => setTransactionForm({...transactionForm, category: v})}
                          options={[
                            { value: 'personal_items', label: 'Personal Items' },
                            { value: 'activities', label: 'Activities' },
                            { value: 'medical_supplies', label: 'Medical Supplies' },
                            { value: 'clothing', label: 'Clothing' },
                            { value: 'entertainment', label: 'Entertainment' },
                            { value: 'family_allowance', label: 'Family Allowance' },
                            { value: 'pension', label: 'Pension' },
                            { value: 'other', label: 'Other' },
                          ]}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Amount</label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={transactionForm.amount}
                          onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
                          required
                          className="block w-full pl-7 pr-12 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <input
                        type="text"
                        value={transactionForm.description}
                        onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Brief description of the transaction"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Notes (optional)</label>
                      <textarea
                        value={transactionForm.notes}
                        onChange={(e) => setTransactionForm({...transactionForm, notes: e.target.value})}
                        rows={3}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Any additional notes..."
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:col-start-2 sm:text-sm"
                  >
                    Create Transaction
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowTransactionModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Finance;
